import {
  Alert,
  Box,
  Button,
  ButtonProps,
  Icon,
  IconButton,
  Image,
  Input,
  InputGroup,
  Stack,
} from "@chakra-ui/react";
import "@aws-amplify/ui-react/styles.css";
import logo from "@keita/lanepatrol-sdk/assets/logo/logo-vertical.png";
import { FormEventHandler, useEffect, useState } from "react";
import {
  confirmResetPassword,
  confirmSignIn,
  resetPassword,
  signIn,
} from "aws-amplify/auth";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { useStores } from "../../shared/stores/RootStore";
import { ViewIcon, ViewOffIcon } from "../../shared/views/icons";

export const AuthenticatorView = () => {
  return (
    <Stack
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      flexGrow={1}
      p={6}
      gap={6}
    >
      <Image src={logo} alt="keita logo" width="200px" />
      <Box width="360px">
        <Authenticator />
      </Box>
    </Stack>
  );
};

type Step =
  | "signIn"
  | "newPassword"
  | "forgotPassword"
  | "forgotPasswordConfirm";

function Authenticator() {
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const { authStore } = useStores();

  useEffect(() => {
    if (authStatus === "authenticated") {
      authStore.onSignIn();
    }
    // eslint-disable-next-line
  }, [authStatus]);

  const [step, setStep] = useState<Step>("signIn");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [viewPassword, setViewPassword] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSignIn: FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await signIn({ username: email, password });
      if (
        result.nextStep.signInStep ===
        "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
      ) {
        setStep("newPassword");
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleNewPassword: FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await confirmSignIn({
        challengeResponse: newPassword,
        options: { userAttributes: {} },
      });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword: FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await resetPassword({ username: email });
      setStep("forgotPasswordConfirm");
      setNewPassword("");
      setConfirmationCode("");
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordConfirm: FormEventHandler<HTMLFormElement> = async (
    e,
  ) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode,
        newPassword,
      });
      setStep("signIn");
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const mainButtonProps: ButtonProps = {
    colorPalette: "primary",
    loading: loading,
  };

  const secondaryButtonProps: ButtonProps = {
    colorPalette: "primary",
    variant: "plain",
    fontSize: "sm",
  };

  const ViewPasswordButton = () => (
    <IconButton
      variant={"plain"}
      colorPalette={"primary"}
      onClick={() => setViewPassword((x) => !x)}
    >
      <Icon as={viewPassword ? ViewOffIcon : ViewIcon} />
    </IconButton>
  );

  const ErrorMessage = () =>
    error && (
      <Alert.Root fontSize={"sm"} status={"error"}>
        <Alert.Indicator />
        <Alert.Description>{error}</Alert.Description>
      </Alert.Root>
    );

  if (step === "newPassword")
    return (
      <form onSubmit={handleNewPassword}>
        <Stack>
          <h2>Set your new password</h2>
          <InputGroup endElement={<ViewPasswordButton />}>
            <Input
              required
              type={viewPassword ? "text" : "password"}
              placeholder="New password"
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </InputGroup>
          <ErrorMessage />

          <Button {...mainButtonProps} type="submit">
            Set Password
          </Button>
        </Stack>
      </form>
    );

  if (step === "forgotPassword")
    return (
      <form onSubmit={handleForgotPassword}>
        <Stack>
          <h2>Reset your password</h2>
          <Input
            required
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <ErrorMessage />
          <Button {...mainButtonProps} type="submit">
            Send Code
          </Button>
          <Button {...secondaryButtonProps} onClick={() => setStep("signIn")}>
            Back to Sign In
          </Button>
        </Stack>
      </form>
    );

  if (step === "forgotPasswordConfirm")
    return (
      <form onSubmit={handleForgotPasswordConfirm}>
        <Stack>
          <h2>Enter the code we sent to your email</h2>
          <Input
            value={confirmationCode}
            required
            type="text"
            placeholder="Confirmation code"
            onChange={(e) => setConfirmationCode(e.target.value)}
          />
          <InputGroup endElement={<ViewPasswordButton />}>
            <Input
              value={newPassword}
              required
              type={viewPassword ? "text" : "password"}
              placeholder="New password"
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </InputGroup>
          <ErrorMessage />

          <Button {...mainButtonProps} type="submit">
            Reset Password
          </Button>
        </Stack>
      </form>
    );

  return (
    <form onSubmit={handleSignIn}>
      <Stack>
        <Input
          required
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <InputGroup endElement={<ViewPasswordButton />}>
          <Input
            required
            type={viewPassword ? "text" : "password"}
            placeholder="Password"
            onChange={(e) => setPassword(e.target.value)}
          />
        </InputGroup>
        <ErrorMessage />

        <Button {...mainButtonProps} type="submit">
          Sign In
        </Button>
        <Button
          {...secondaryButtonProps}
          onClick={() => setStep("forgotPassword")}
        >
          Forgot Password?
        </Button>
      </Stack>
    </form>
  );
}
